import React, { useEffect, useState } from 'react';
import Modal from '../../../../components/molecules/Modal/Modal';
import { Deductible, FftItem } from '../../../../@types/FFT.type';
import { CircularProgress } from '@mui/material';
import Heading from '../../../../components/atoms/Heading';
import Text from '../../../../components/atoms/Text';
import { getPrefixMonth } from '../../../../shared/utils/dates.util';
import { DATES } from '../../../../shared/utils/dates';
import { OPERATION_TYPES } from '../../../../shared/constants/fiscalFlowTransaction';
import { XCircle, CheckCircle } from '@phosphor-icons/react';

interface DetailModalProps {
	isOpen: boolean;
	onClose: () => void;
	analysisData: Deductible[];
	isLoadingAnalysis: boolean;
	item: FftItem;
}

const formatPeriod = (period: string) => {
	if (!period) return '';
	const [year, month] = period.split('-');
	const months = [
		'Enero',
		'Febrero',
		'Marzo',
		'Abril',
		'Mayo',
		'Junio',
		'Julio',
		'Agosto',
		'Septiembre',
		'Octubre',
		'Noviembre',
		'Diciembre',
	];
	return `${months[parseInt(month) - 1]} ${year}`;
};

const DetailModal: React.FC<DetailModalProps> = ({
	isOpen,
	onClose,
	analysisData,
	isLoadingAnalysis,
	item,
}) => {
	const [counterDeductible, setCounterDeductible] = useState(0);
	const [counterNotDeductible, setCounterNotDeductible] = useState(0);
	const [counterTotal, setCounterTotal] = useState(0);

	useEffect(() => {
		setCounterDeductible(
			analysisData.filter((deductible) => deductible.isInvoiceDeductible)
				.length,
		);
		setCounterNotDeductible(
			analysisData.filter((deductible) => !deductible.isInvoiceDeductible)
				.length,
		);
		setCounterTotal(analysisData.length);
	}, [analysisData]);
	return (
		<Modal
			open={isOpen}
			onClose={onClose}
		>
			<div className='p-4 max-h-[80vh] overflow-y-auto mt-[24px]'>
				<Heading
					size='xs'
					weight='bold'
					color='darkBlue'
				>
					{item?.operation_type === OPERATION_TYPES.MONTHLY
						? 'Declaración mensual'
						: 'Declaración anual'}
				</Heading>
				<Heading
					size='xs'
					weight='light'
					color='dark'
				>
					Plataformas tecnológicas, periodo {formatPeriod(item?.period)}
				</Heading>
				<div className='flex gap-x-2 mb-[48px] mt-[8px]'>
					<Text
						size='mini-1'
						weight='light'
						color='medium'
					>
						Fecha estimada de entrega:
					</Text>
					<Text
						size='mini-1'
						weight='light'
					>
						{`17 al 22 ${getPrefixMonth(DATES.currentMonth)} ${DATES.currentYear}`}
					</Text>
				</div>
				<Text
					size='body-3'
					weight='medium'
					color='medium'
					sx='mb-[16px]'
				>
					Deducciones
				</Text>
				<Text
					size='caption'
					weight='light'
					sx='mb-[16px]'
				>
					Aquí encontrarás el detalle de todas tus facturas y deducibles encontrados.
					<br />
					Ten la seguridad de que nuestro sistema analiza cuidadosamente cada
					una para maximizar tus deducciones de manera correcta y segura.
				</Text>
				<div className='flex gap-x-[20px] mb-[24px]'>
					<div className='flex items-center gap-x-2'>
						<div className='w-2.5 h-2.5 bg-[#6aa132] rounded-full' />
						<Text
							size='body-3'
							weight='light'
						>
							Deducibles: {counterDeductible}
						</Text>
					</div>
					<div className='flex items-center gap-x-2'>
						<div className='w-2.5 h-2.5 bg-[#da1e28] rounded-full' />
						<Text
							size='body-3'
							weight='light'
						>
							No deducibles: {counterNotDeductible}
						</Text>
					</div>
					<div className='flex items-center gap-x-2'>
						<Text
							size='body-3'
							weight='light'
						>
							Total: {counterTotal}
						</Text>
					</div>
				</div>
				{isLoadingAnalysis ? (
					<div className='flex justify-center items-center h-full'>
						<CircularProgress />
					</div>
				) : (
					analysisData.map((deductible, index) => (
						<div
							key={index}
							className={`${deductible.isInvoiceDeductible ? 'border-[#6aa132]' : 'border-[#da1e28]'} p-[16px] border-[1px] rounded-[8px] mb-[8px] border-l-[6px] flex gap-2`}
						>
							<div className='w-6 h-6 flex items-center justify-center flex-shrink-0'>
							{deductible.isInvoiceDeductible ? (
								<CheckCircle
									size={24}
									weight='duotone'
									color='#6aa132'
								/>
							) : (
								<XCircle
									size={24}
									weight='duotone'
									color='#da1e28'
								/>
								)}
							</div>
							<div className='flex flex-col'>
								<Text
									size='body-3'
									weight='medium'
									color='medium'
								>
									Factura #{deductible?.invoiceId}
								</Text>
								{deductible.items && deductible.items.map((item, idx) => (
									<div key={idx}>
										<Text
											size='caption'
											weight='light'
											color='medium'
										>
											{item.description}
										</Text>
										<Text
											size='caption'
											weight='light'
											color='medium'
											sx='mt-[8px]'
										>
											Razón: {item.reason}
										</Text>
									</div>
								))}
							</div>
						</div>
					))
				)}
			</div>
		</Modal>
	);
};

export default DetailModal;
